import './memory.css';

// The engine ships as a global (window.MemoryGame) loaded via a <script> tag — not as an
// ES module — mirroring how the standalone build (../sephora-games/standalone/memory)
// wires it. The URL comes from the modal's data-engine-src (asset() → manifest + CDN);
// the literal below is only a fallback for a stale template.
const SCRIPT_URL_FALLBACK = '/build/games/memory/memory.min.js';

// ── Image assets ──────────────────────────────────────────────────────────────
// Imported through webpack so they get hashed/published. require.context (rather than a
// static import) keeps the build green if one of these is missing: it simply resolves to
// null and the engine falls back (a missing card back becomes a flat cardBackColor).
//
// NOTE: only the CHROME lives here — currently just the shared card BACK. The card
// FACES are uploaded in the admin (App\Entity\MemoryCard) and served by the backend: the
// engine runs in `reveal` mode, so a face URL is only handed out when that specific card
// is flipped, which is what stops the board from being solvable by reading the DOM.
const uiCtx = require.context('./images', false, /\.(webp|svg|png)$/);

const resolveAsset = (mod) => (mod && mod.default) ? mod.default : mod;

const ui = {};
uiCtx.keys().forEach((key) => {
    const name = key.replace('./', '').replace(/\.(webp|svg|png)$/, '');
    ui[name] = resolveAsset(uiCtx(key));
});

/**
 * Whole-game time budget. MUST mirror MemoryController::GAME_CONFIG['timeLimitSeconds'].
 *
 * The engine's own `timeLimitSeconds` is PER ROUND, which is not what the rules describe —
 * so the countdown is owned here instead: one budget across all three boards, paused
 * alongside the game (see pauseMemory) and ending it via forceEnd() when it runs out.
 */
const BUDGET_MS = 10 * 60 * 1000;

/** How often the budget is re-evaluated and reported to the modal countdown. */
const BUDGET_TICK_MS = 250;

// autoStart: true → the engine runs straight into play; the surrounding modal owns the
// loading / instructions / result / answer screens (see the modal handler).
const GAME_CONFIG = {
    // Opaque tokens from /start; image + pair group fetched on first flip. Server-authoritative.
    cardSource: 'reveal',
    autoStart: true,

    // Columns come per round from /start; these are the fallbacks.
    grid: {
        breakpoints: [
            {maxCards: 6, cols: 3},
            {maxCards: 12, cols: 4},
        ],
        defaultCols: 4,
    },

    // No per-round limit — the 10-minute budget above spans the whole game.
    timeLimitSeconds: null,
    timeoutBehaviour: 'end',
    previewSeconds: 0,

    resolveDelayMs: 500,
    mismatchDelayMs: 500,
    roundCompleteDelayMs: 600,

    // No level-up interstitial: finishing a board goes straight into the next one (and the
    // last board straight to the result screen). The engine branches on this in
    // _onRoundComplete, so nothing in the engine itself needs patching.
    showBetweenRounds: false,

    // Mirrors MemoryController::GAME_CONFIG — shown live in the HUD. /result re-scores from
    // the server's own token map, so these values are advisory for display only.
    scoring: {
        pointsPerPair: 10,
        roundCompleteBonus: 0,
        mistakePenalty: 2,
        floorZero: true,
    },

    heartbeatInterval: 10000,
};

// Branding / assets config. The engine turns `colors` and `tokens` into --mem-* CSS
// variables, so anything not expressible here is overridden in s-game-modal.scss.
function buildAssetsConfig() {
    return {
        theme: 'tyskie',

        tokens: {
            fontFamily: "'Tyskie PRO', 'Arial Black', sans-serif",
        },

        // The HUD is rendered entirely by the modal (score, countdown, level dots) — the level
        // indicator needs one element per level, which the engine's text widgets can't express,
        // and the countdown spans all rounds rather than one. Both rows stay empty, and
        // `.mem-topbar:empty` / `.mem-banner:empty` collapse them to nothing.
        hud: {
            topbar: [],
            banner: [],
            overlayClose: false,
        },

        texts: {
            gameTitle: 'Odkrywaj Tyskie Zero',
            gameSubtitle: 'Znajdź wszystkie pary!',
            startButton: 'Graj',
            loading: 'Ładowanie...',
            errorPrefix: 'Błąd:',
            close: 'Zamknij',

            roundHeading: 'Odkryj wszystkie pary na planszy',

            hudLevel: 'POZIOM',
            hudScore: 'PKT',
            hudPairs: 'PARY',
            hudMistakes: 'BŁĘDY',

            // The between-rounds overlay is disabled (showBetweenRounds: false); these stay
            // only so re-enabling it doesn't produce a blank card.
            betweenTitle: 'Idzie Ci świetnie!',
            betweenText: 'Przygotuj się na 2 razy więcej kart\ndo zapamiętania.',
            betweenButton: 'Następny poziom',

            resultTitle: 'Twój wynik',
            resultTitleTimeout: 'Czas minął!',
            resultPairs: 'Znalezione pary',
            resultMistakes: 'Błędy',
            resultTime: 'Czas',
            resultScore: 'Punkty',
            playAgain: 'Zagraj ponownie',
        },

        images: {
            background: null,               // the modal frame already paints the branded background
            cardBack: ui['card-back'] ?? null,
            betweenIcon: null,              // the between-rounds overlay never shows
            logo: null,
        },

        sounds: {
            flip: null,
            match: null,
            mismatch: null,
            roundComplete: null,
            gameOver: null,
            countdown: null,
        },

        colors: {
            bgStart: '#F0F0F0',
            bgMid: '#F0F0F0',
            bgEnd: '#F0F0F0',
            btnGradient: 'linear-gradient(0deg, #D20007 0%, #D20007 100%)',
            surface: '#FFFFFF',
            onSurface: '#0C2652',
            text: '#0C2652',
            outline: '#0C2652',
            levelBg: '#0C2652',
            levelLabelBg: '#FFFFFF',
            timerBg: '#1F69B2',
            timerText: '#FFFFFF',
            timerDanger: '#D20007',
            closeBg: '#0C2652',
            closeIcon: '#FFFFFF',
            cardBackColor: 'transparent',
            matched: '#1F69B2',
            wrong: '#D20007',
            scrim: 'rgba(12, 38, 82, 0.75)',
        },
    };
}

function apiBase(uuid) {
    return `/api/application/${uuid}/memory`;
}

/**
 * Preload the game chrome so the first board paints without a flash. `onProgress` is called
 * with a 0..1 fraction as each image settles (load or error), driving the loading screen's
 * 10-indicator progress bar. Resolves once everything has settled.
 *
 * Card faces are NOT preloaded: in `reveal` mode their URLs only exist server-side and are
 * handed out one flip at a time — that is the point of the mode. The engine's 500 ms
 * resolve delay absorbs the per-card fetch.
 */
export function preloadMemoryAssets(onProgress = () => {}) {
    const urls = Object.values(ui).filter(Boolean);
    const total = urls.length || 1;
    let settled = 0;

    const loadOne = (url) => new Promise((res) => {
        const img = new Image();
        const settle = () => {
            settled += 1;
            onProgress(Math.min(1, settled / total));
            res();
        };
        img.onload = settle;
        img.onerror = settle;
        img.src = url;
    });

    return Promise.all(urls.map(loadOne)).then(() => {
        onProgress(1);
    });
}

let gameInstance = null;

/**
 * Number of levels in the session. /start reports it via `gameConfig.levels`, which the
 * engine merges into its own public `gameConfig` — so the HUD never has to duplicate the
 * server's ROUNDS. Falls back to the local round config if the server said nothing.
 */
function levelCount() {
    return Number(gameInstance?.gameConfig?.levels) || 3;
}

// ── Whole-game time budget ────────────────────────────────────────────────────
let budgetTicker = null;
let budgetUsedMs = 0;      // time accumulated across completed run segments
let budgetSince = null;    // performance.now() when the current segment started
let timedOut = false;      // true once the budget forced the game to end
let onTimeLeft = null;     // modal callback rendering the countdown

function budgetElapsedMs() {
    return budgetUsedMs + (budgetSince !== null ? performance.now() - budgetSince : 0);
}

function budgetTick() {
    const remainingMs = Math.max(0, BUDGET_MS - budgetElapsedMs());

    if (onTimeLeft) {
        onTimeLeft(remainingMs);
    }

    if (remainingMs > 0) {
        return;
    }

    // Out of time: stop the clock and let the engine close the session normally, so the
    // attempts made so far are still submitted to /result and scored.
    timedOut = true;
    stopBudget();
    try {
        gameInstance?.forceEnd?.();
    } catch { /* ignore */ }
}

function startBudget() {
    budgetUsedMs = 0;
    budgetSince = performance.now();
    timedOut = false;
    stopBudgetTicker();
    budgetTicker = setInterval(budgetTick, BUDGET_TICK_MS);
    budgetTick();
}

function stopBudgetTicker() {
    if (budgetTicker !== null) {
        clearInterval(budgetTicker);
        budgetTicker = null;
    }
}

function stopBudget() {
    stopBudgetTicker();
    if (budgetSince !== null) {
        budgetUsedMs += performance.now() - budgetSince;
        budgetSince = null;
    }
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (window.MemoryGame || document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

/**
 * Mount and run the game inside `container`.
 *
 * The engine drives /start, /reveal, /heartbeat, /level and /result itself; the score is
 * registered server-side by /result and echoed back, so `onDone(score, reason)` reports the
 * authoritative value (the instance is already torn down by then).
 *
 * `reason` is 'timeup' when the 10-minute budget ended the game, otherwise the engine's own
 * reason ('completed' | 'forced'). The third argument is the merged result object, which
 * carries the rest of the server's response (maxScore, pairsFound, mistakes).
 * `onTimeLeftCallback(remainingMs)` fires ~4×/s so the modal can render the countdown.
 */
export function startMemory(container, {
    uuid = null,
    scriptUrl = null,
    onDone = () => {},
    onError = null,
    onTimeLeft: onTimeLeftCallback = null,
    onScore = null,
    onLevel = null,
} = {}) {
    teardownMemory();
    container.classList.add('mem-game');

    onTimeLeft = onTimeLeftCallback;

    const apiConfig = {
        useMock: false,
        endpoints: {
            startGame: `${apiBase(uuid)}/start`,
            revealCard: `${apiBase(uuid)}/reveal`,
            submitLevel: `${apiBase(uuid)}/level`,
            heartbeat: `${apiBase(uuid)}/heartbeat`,
            submitResult: `${apiBase(uuid)}/result`,
        },
    };

    loadScript(scriptUrl || SCRIPT_URL_FALLBACK)
        .then(() => {
            if (!window.MemoryGame) {
                console.error('MemoryGame: script loaded but global is not defined');
                onDone(0);
                return;
            }

            gameInstance = window.MemoryGame.init(container.id, {
                assetsConfig: buildAssetsConfig(),
                gameConfig: GAME_CONFIG,
                apiConfig,
                callbacks: {
                    // Any callback key that isn't one of the engine's aliases is bound to the
                    // event bus verbatim — so this hooks the real start of play, i.e. after
                    // /start succeeded and the first board is up.
                    'game:start': () => {
                        startBudget();
                        // /start merged its gameConfig by now, so the level count is known.
                        onScore?.(0);
                        onLevel?.(0, levelCount());
                    },

                    // The modal owns the HUD, so the running score and the current level are
                    // pushed out as the engine reports them.
                    'round:start': (e) => onLevel?.(e?.roundIndex ?? 0, levelCount()),
                    'pair:matched': (e) => onScore?.(e?.score ?? 0),
                    'pair:missed': (e) => onScore?.(e?.score ?? 0),

                    onComplete: (result) => {
                        // /result is authoritative and its response is merged over the client
                        // payload by the engine, so result.score is the server's number.
                        const score = result?.score ?? 0;
                        const reason = timedOut ? 'timeup' : (result?.reason ?? null);
                        teardownMemory();
                        onDone(score, reason, result ?? null);
                    },

                    onError: (error) => {
                        console.error('MemoryGame error:', error);
                        teardownMemory();
                        // A failed /start (outside play hours, already played, expired window)
                        // surfaces here as an ApiError carrying { status, data }. Hand it to the
                        // caller so it can show a dedicated screen; otherwise fall back to a
                        // neutral score-0 result.
                        if (onError) {
                            onError(error);
                        } else {
                            onDone(0);
                        }
                    },
                },
            });
        })
        .catch((e) => {
            console.error('MemoryGame: failed to load game script', e);
            onDone(0);
        });
}

export function teardownMemory() {
    stopBudget();
    onTimeLeft = null;

    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */ }
        gameInstance = null;
    }
}

/** Pause a running game and its time budget (no-op unless the engine is playing). */
export function pauseMemory() {
    try {
        gameInstance?.pause?.();
    } catch { /* ignore */ }
    stopBudget();
}

/** Resume a paused game and restart the budget clock (no-op unless the engine is paused). */
export function resumeMemory() {
    try {
        gameInstance?.resume?.();
    } catch { /* ignore */ }

    if (gameInstance && budgetSince === null && !timedOut) {
        budgetSince = performance.now();
        stopBudgetTicker();
        budgetTicker = setInterval(budgetTick, BUDGET_TICK_MS);
    }
}
